import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchCarouselItems } from '../../services/carousel';

// Load carousel from localStorage
const loadCarouselFromStorage = () => {
  try {
    const carouselData = localStorage.getItem('carousel');
    return carouselData ? JSON.parse(carouselData) : null;
  } catch (error) {
    console.error('Error loading carousel from storage:', error);
    return null;
  }
};

// Save carousel to localStorage
const saveCarouselToStorage = (carousel) => {
  try {
    localStorage.setItem('carousel', JSON.stringify(carousel));
  } catch (error) {
    console.error('Error saving carousel to storage:', error);
  }
};

// Async thunk to fetch carousel items
export const loadCarousel = createAsyncThunk(
  'carousel/loadCarousel',
  async (_, { rejectWithValue }) => {
    try {
      const items = await fetchCarouselItems();
      return items;
    } catch (error) {
      // If API fails, try to load from localStorage
      const storedCarousel = loadCarouselFromStorage();
      if (storedCarousel && storedCarousel.items && storedCarousel.items.length > 0) {
        // Return stored data as fallback
        return storedCarousel.items;
      }
      return rejectWithValue(error.response?.data?.message || 'Failed to load carousel');
    }
  }
);

const getInitialItems = () => {
  const stored = loadCarouselFromStorage()?.items || [];
  return stored.filter((item, i, arr) => arr.findIndex((x) => x.id === item.id) === i);
};

const initialState = {
  items: getInitialItems(),
  loading: false,
  error: null,
  lastFetched: loadCarouselFromStorage()?.lastFetched || null,
};

const carouselSlice = createSlice({
  name: 'carousel',
  initialState,
  reducers: {
    clearCarousel: (state) => {
      state.items = [];
      state.error = null;
      state.lastFetched = null;
      localStorage.removeItem('carousel');
    },
  },
  extraReducers: (builder) => {
    builder
      // Loading carousel
      .addCase(loadCarousel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loadCarousel.fulfilled, (state, action) => {
        state.loading = false;
        const raw = action.payload || [];
        // Dedupe by id so only one slide per carousel item
        state.items = raw.filter((item, i, arr) => arr.findIndex((x) => x.id === item.id) === i);
        state.error = null;
        state.lastFetched = new Date().toISOString();
        
        // Persist to localStorage
        saveCarouselToStorage({
          items: state.items,
          lastFetched: state.lastFetched,
        });
      })
      .addCase(loadCarousel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Failed to load carousel';
        
        // If we have stored data, use it even if API failed
        const storedCarousel = loadCarouselFromStorage();
        if (storedCarousel && storedCarousel.items && storedCarousel.items.length > 0) {
          state.items = storedCarousel.items;
          state.lastFetched = storedCarousel.lastFetched;
        }
      });
  },
});

export const { clearCarousel } = carouselSlice.actions;

// Selectors
export const selectCarouselItems = (state) => state.carousel.items;
export const selectCarouselLoading = (state) => state.carousel.loading;
export const selectCarouselError = (state) => state.carousel.error;
export const selectCarouselLastFetched = (state) => state.carousel.lastFetched;

export default carouselSlice.reducer;
